
import { useState, useEffect } from "react";
import { Layout } from "@/components/Layout";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { Heart, MessageCircle, Users, Bell } from "lucide-react";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Button } from "@/components/ui/button";

interface Notification {
  id: string;
  message: string;
  type: "like" | "comment" | "group" | "mention";
  read_status: boolean;
  timestamp: string;
}

const Notifications = () => {
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { toast } = useToast();

  // Fetch notifications from Supabase
  useEffect(() => {
    const fetchNotifications = async () => {
      try {
        setLoading(true);
        setError(null);
        
        const { data: { session } } = await supabase.auth.getSession();
        
        if (!session?.user) {
          setError("Please sign in to view notifications");
          return;
        }

        const { data, error: notificationsError } = await supabase
          .from('notifications')
          .select('*')
          .eq('user_id', session.user.id)
          .order('created_at', { ascending: false });

        if (notificationsError) {
          console.error('Error fetching notifications:', notificationsError);
          setError(notificationsError.message);
          toast({
            title: "Error fetching notifications",
            description: notificationsError.message,
            variant: "destructive",
          });
          return;
        }

        if (data) {
          setNotifications(data.map(notification => ({
            id: notification.id,
            message: notification.content,
            type: notification.type as "like" | "comment" | "group" | "mention",
            read_status: notification.read_status || false,
            timestamp: new Date(notification.created_at).toLocaleString()
          })));
        }
      } catch (error) {
        console.error('Error in fetchNotifications:', error);
        setError("An unexpected error occurred");
      } finally {
        setLoading(false);
      }
    };

    fetchNotifications();

    // Subscribe to new notifications
    const subscribeToNotifications = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.user) return;

      const channel = supabase
        .channel('schema-db-changes')
        .on(
          'postgres_changes',
          {
            event: 'INSERT',
            schema: 'public',
            table: 'notifications',
            filter: `user_id=eq.${session.user.id}`
          },
          (payload) => {
            const newNotification = {
              id: payload.new.id,
              message: payload.new.content,
              type: payload.new.type as "like" | "comment" | "group" | "mention",
              read_status: payload.new.read_status || false,
              timestamp: new Date(payload.new.created_at).toLocaleString()
            };
            setNotifications(prev => [newNotification, ...prev]);
          }
        )
        .subscribe();

      return () => {
        supabase.removeChannel(channel);
      };
    };

    subscribeToNotifications();
  }, [toast]);

  const markAsRead = async (id: string) => {
    try {
      const { error } = await supabase
        .from('notifications')
        .update({ read_status: true })
        .eq('id', id);

      if (error) {
        toast({
          title: "Error marking notification as read",
          description: error.message,
          variant: "destructive",
        });
        return;
      }

      setNotifications(prev => 
        prev.map(notification => 
          notification.id === id 
            ? { ...notification, read_status: true } 
            : notification
        )
      );
    } catch (error) {
      console.error('Error marking notification as read:', error);
    }
  };

  const markAllAsRead = async () => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      
      if (!session?.user) {
        setError("Please sign in to mark notifications as read");
        return;
      }

      const { error } = await supabase
        .from('notifications')
        .update({ read_status: true })
        .eq('user_id', session.user.id)
        .is('read_status', false);

      if (error) {
        toast({
          title: "Error marking all notifications as read",
          description: error.message,
          variant: "destructive",
        });
        return;
      }

      setNotifications(prev => 
        prev.map(notification => ({ ...notification, read_status: true }))
      );
    } catch (error) {
      console.error('Error marking all notifications as read:', error);
    }
  };

  const getIcon = (type: Notification["type"]) => {
    switch (type) {
      case "like":
        return <Heart className="w-5 h-5 text-pink-500" />;
      case "comment":
        return <MessageCircle className="w-5 h-5 text-blue-500" />;
      case "group":
        return <Users className="w-5 h-5 text-green-500" />;
      default:
        return <Bell className="w-5 h-5" />;
    }
  };

  return (
    <Layout>
      <div className="max-w-2xl mx-auto">
        <div className="flex justify-between items-center mb-6">
          <h1 className="text-2xl font-bold">Notifications</h1>
          {notifications.length > 0 && (
            <Button 
              variant="outline"
              onClick={markAllAsRead}
            >
              Mark all as read
            </Button>
          )}
        </div>

        {loading ? (
          <div className="p-8 text-center text-gray-500">
            Loading notifications...
          </div>
        ) : error ? (
          <div className="p-8 text-center text-red-500">
            {error}
          </div>
        ) : notifications.length === 0 ? (
          <div className="p-8 text-center text-gray-500 bg-white dark:bg-gray-800 rounded-lg shadow">
            No notifications to display
          </div>
        ) : (
          <ScrollArea className="h-[70vh]">
            <div className="space-y-3">
              {notifications.map((notification) => (
                <div 
                  key={notification.id}
                  className={`p-4 rounded-lg shadow flex items-start gap-3 bg-white dark:bg-gray-800 ${!notification.read_status ? 'border-l-4 border-blue-500' : ''}`}
                  onClick={() => !notification.read_status && markAsRead(notification.id)}
                >
                  <div className="bg-gray-100 dark:bg-gray-700 p-2 rounded-full">
                    {getIcon(notification.type)}
                  </div>
                  <div className="flex-1">
                    <p className="text-sm">{notification.message}</p>
                    <span className="text-xs text-gray-500 dark:text-gray-400">
                      {notification.timestamp}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </ScrollArea>
        )}
      </div>
    </Layout>
  );
};

export default Notifications;
